import { FormEvent, useEffect, useState } from 'react';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import useSafeLogger from 'src/hooks/useSafeLogger';
import styles from './StripeElements.module.scss';
import cn from 'classnames';
import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import {
  useBanner,
  useColorTheme,
  useGetUser,
  useSdkLoader,
  useSession,
  useVisible,
} from 'src/hooks';
import {
  useCreateSubscriptionMutation,
  useUpgradeSubscriptionMutation,
} from 'src/store/services';
import { env } from 'src/env';
import { toast } from 'react-toastify';
import {
  PAYMENT_ERROR,
  PAYMENT_ERROR_MESSAGE,
  REFERRAL_ID,
  TRANSACTION_ID,
} from 'src/constants';
import {
  AppRoutes,
  GTMEvent,
  NinjaSubscriptionPlan,
  SubscriptionPlanInfo,
} from 'src/types';
import { PriceDiscountType } from 'src/types/wallet/models/PriceDiscountType';
import { useNavigate } from 'react-router-dom';
import {
  getReferralId,
  isSuperNinjaPlan,
  sendGTMEvent,
  sendPaymentGTMEvent,
} from 'src/utils';
import { useChoosePlanModal } from 'src/hooks/useChoosePlanModal';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { PaymentProgressBar } from '../../componentsV2/PaymentProgressBar';
import { Button } from 'src/v2/commonComponents/Button';

const PAYMENT_PROVIDER = 'stripe';

interface StripeElementsProps {
  selectedPlan?: SubscriptionPlanInfo;
  onPaymentProcessing: (value: boolean) => void;
  numberOfSeats?: number;
  appliedCoupon?: string;
  onSdkReady?: () => void;
}

export const StripeElements = ({
  selectedPlan,
  onPaymentProcessing,
  numberOfSeats,
  appliedCoupon,
  onSdkReady,
}: StripeElementsProps) => {
  const { pgLogError, pgLogInfo } = useSafeLogger();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { isEducationUser } = useGetUser();
  const {
    shouldShowPlanModal,
    changeShouldShowPlanModal,
    changeNeverShowChoosePlanModalAgain,
  } = useChoosePlanModal();

  const { removeAllPaymentBanners } = useBanner();
  const { isDarkTheme } = useColorTheme();
  const canRedirectToSN = true;

  const shouldRedirectToSNAfterExternalPayment =
    useFeatureFlagEnabled('paymentRedirect');

  const enableHandlingOfBusinessLogicInSN =
    useFeatureFlagEnabled('handleBusinessInSN');

  const isSNPlan =
    isSuperNinjaPlan(selectedPlan?.plan_family) && canRedirectToSN;

  const referralId = getReferralId();

  const {
    appUser: { user_id, email },
  } = useSession();

  const [createSubscription] = useCreateSubscriptionMutation();
  const [upgradeSubscription, { isLoading: isCreatingCheckout }] =
    useUpgradeSubscriptionMutation();

  const [isCashAppButton, setIsCashAppButton] = useState<boolean>(false);

  const {
    timedOut: isStripeSdkTimedOut,
    loading: isStripeSdkLoading,
    start: startStripeSdk,
    resolve: resolveStripeSdk,
    reset: resetStripeSdk,
  } = useSdkLoader();

  useEffect(() => {
    startStripeSdk();

    return () => {
      resetStripeSdk();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isReadyExpressCheckout, setReadyExpressCheckout] =
    useState<boolean>(false);
  const [isReadyPaymentElement, setReadyPaymentElement] =
    useState<boolean>(false);

  useEffect(() => {
    if (isReadyExpressCheckout && isReadyPaymentElement) {
      resolveStripeSdk();
      onSdkReady?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyExpressCheckout, isReadyPaymentElement]);

  useEffect(() => {
    if (isStripeSdkTimedOut) {
      pgLogError('stripe sdk timed out', {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'stripe sdk timed out',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStripeSdkTimedOut]);

  const {
    isVisible: isSubmitDisabled,
    onVisibilitySet: setSubmitDisabled,
    onVisibilityRemove: setSubmitEnabled,
  } = useVisible(true);

  const handleCreateSubscription = async () => {
    if (!selectedPlan) return;
    try {
      const result = await createSubscription({
        user_id,
        seat_count: numberOfSeats,
        // TODO(olha): BE is not ready for getting new lookupKey
        plan_id: selectedPlan?.plan_id,
        stripe_promotion_code:
          selectedPlan?.metadata?.stripe?.coupon_metadata?.promotion_code ||
          undefined,
        referral_id:
          !!referralId &&
          selectedPlan.plan_tier !== NinjaSubscriptionPlan.TEAMS_ULTRA
            ? referralId
            : undefined,
        discount_type: isEducationUser
          ? PriceDiscountType.STUDENT_DISCOUNT
          : undefined,
      }).unwrap();

      localStorage.removeItem(REFERRAL_ID);
      return result;
    } catch (error) {
      onPaymentProcessing(false);
      toast.error(PAYMENT_ERROR_MESSAGE);

      pgLogError(PAYMENT_ERROR, {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'create subscription',
        error: JSON.stringify(error),
      });

      return;
    }
  };

  // TODO(olha): includes code-duplication. Needs refactoring
  const handleConfirmPayment = async () => {
    if (!elements || !stripe) {
      return;
    }

    const subscriptionIntent = await handleCreateSubscription();

    if (!subscriptionIntent) {
      return;
    }

    const { client_secret, subscription_id, flow } = subscriptionIntent;

    localStorage.setItem(TRANSACTION_ID, subscription_id);

    const canSendToSN =
      shouldRedirectToSNAfterExternalPayment &&
      !selectedPlan?.plan_tier?.includes('business');

    const returnUrl = `${
      canSendToSN && !enableHandlingOfBusinessLogicInSN
        ? `${env.REACT_APP_SUPER_NINJA_URL}dashboard/`
        : enableHandlingOfBusinessLogicInSN
          ? `${env.REACT_APP_SUPER_NINJA_URL}dashboard/`
          : env.REACT_APP_NINJA_UI_URL
    }?subscription_succeeded_plan=${
      selectedPlan?.plan_tier
    }&subscription_succeeded_period=${selectedPlan?.period}&subscription_succeeded_value=${Math.round(
      (selectedPlan?.original_price || 0) / 100,
    )}&subscription_succeeded_currency=${selectedPlan?.currency || 'USD'}${
      numberOfSeats
        ? '&subscription_succeeded_number_of_seats=' + numberOfSeats
        : ''
    }${canSendToSN || enableHandlingOfBusinessLogicInSN ? '&transaction_id=' + subscription_id : ''}`;

    if (flow === 'setup_intent') {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (!isSNPlan) {
        onPaymentProcessing(false);
      }

      localStorage.removeItem(TRANSACTION_ID);

      if (setupIntent && setupIntent.status === 'succeeded') {
        // !!! (olha): it's an important event we are tracking. We need to make sure it calls properly and only once by the subscription
        sendPaymentGTMEvent({
          transaction_id: subscription_id,
          subscription_plan: selectedPlan?.plan_tier,
          subscription_period: selectedPlan?.period,
          number_of_seats: numberOfSeats ? numberOfSeats.toString() : undefined,
          value: Math.round((selectedPlan?.original_price || 0) / 100),
          currency: selectedPlan?.currency || 'USD',
        });

        if (shouldShowPlanModal) {
          changeShouldShowPlanModal(false);
        }

        changeNeverShowChoosePlanModalAgain(true);
        removeAllPaymentBanners();
        if (isSNPlan || enableHandlingOfBusinessLogicInSN) {
          window.location.href = env.REACT_APP_SUPER_NINJA_URL;
        } else {
          navigate(AppRoutes.HOME, { replace: true });
        }
      } else {
        toast.error(PAYMENT_ERROR_MESSAGE);

        pgLogError(PAYMENT_ERROR, {
          user_id,
          payment_provider: PAYMENT_PROVIDER,
          plan: selectedPlan?.plan_id,
          step: 'confirm payment',
          error: JSON.stringify(error),
        });
      }
    } else {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (!isSNPlan) {
        onPaymentProcessing(false);
      }

      localStorage.removeItem(TRANSACTION_ID);

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // !!! (olha): it's an important event we are tracking. We need to make sure it calls properly and only once by the subscription
        sendPaymentGTMEvent({
          transaction_id: subscription_id,
          subscription_plan: selectedPlan?.plan_tier,
          subscription_period: selectedPlan?.period,
          number_of_seats: numberOfSeats ? numberOfSeats.toString() : undefined,
          value: Math.round((selectedPlan?.original_price || 0) / 100),
          currency: selectedPlan?.currency || 'USD',
        });

        if (shouldShowPlanModal) {
          changeShouldShowPlanModal(false);
        }

        removeAllPaymentBanners();
        if (isSNPlan || enableHandlingOfBusinessLogicInSN) {
          window.location.href = env.REACT_APP_SUPER_NINJA_URL;
        } else {
          navigate(AppRoutes.HOME, { replace: true });
        }
      } else {
        toast.error(PAYMENT_ERROR_MESSAGE);

        pgLogError(PAYMENT_ERROR, {
          user_id,
          payment_provider: PAYMENT_PROVIDER,
          plan: selectedPlan?.plan_id,
          step: 'confirm payment',
          error: JSON.stringify(error),
        });
      }
    }
  };

  const handleSubmit = async (
    event:
      | StripeExpressCheckoutElementConfirmEvent
      | FormEvent<HTMLFormElement>,
  ) => {
    if ('preventDefault' in event) {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      event.preventDefault();
    }

    if (!elements || !stripe) {
      return;
    }

    onPaymentProcessing(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onPaymentProcessing(false);

      pgLogError(PAYMENT_ERROR, {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'stripe form validation',
        error: JSON.stringify(submitError),
      });

      return;
    }

    await handleConfirmPayment();
  };

  const handlePaymentElementChange = ({
    complete,
    value,
    collapsed,
  }: StripePaymentElementChangeEvent) => {
    if (value.type === 'cashapp') {
      setIsCashAppButton(true);
    } else {
      setIsCashAppButton(false);
    }

    if (complete) {
      setSubmitEnabled();
    } else {
      setSubmitDisabled();
    }

    // !!! (olha): if we change defaultCollapsed to false, GTM event don't work properly
    if (!collapsed) {
      sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
        payment_method_type: value.type,
      });
    }
  };

  const handleExpressCheckoutClick = (
    event: StripeExpressCheckoutElementClickEvent,
  ) => {
    sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
      payment_method_type: event.expressPaymentType,
    });
    event.resolve();
  };

  const handleSubmitClick = () => {
    sendGTMEvent(GTMEvent.PAYMENT_SUBMIT_BUTTON_CLICK);
  };

  const handleFallbackCheckout = async () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }

    // Warning: multiple seats not supported via fallback
    if (!!numberOfSeats && numberOfSeats > 1) {
      toast.error(
        'Please purchase a single seat first, then add additional seats through the upgrade flow.',
      );
      return;
    }

    try {
      pgLogInfo('User triggered fallback checkout', {
        user_id,
        plan_id: selectedPlan?.plan_id,
        number_of_seats: numberOfSeats,
      });

      const result = await upgradeSubscription({
        user_id,
        lookup_key: selectedPlan.plan_name,
        promotion_code: appliedCoupon,
        embedded: false,
      }).unwrap();

      if (result.checkout_session?.url) {
        window.open(
          result.checkout_session.url,
          '_blank',
          'noopener,noreferrer',
        );
      } else if (result.billing_portal_session?.url) {
        window.open(
          result.billing_portal_session.url,
          '_blank',
          'noopener,noreferrer',
        );
      } else {
        throw new Error('No checkout URL returned from API');
      }
    } catch (error) {
      toast.error(
        'Unable to create payment session. Please refresh and try again.',
      );

      pgLogError('Failed to create fallback checkout session', {
        user_id,
        plan_id: selectedPlan?.plan_id,
        number_of_seats: numberOfSeats,
        error: JSON.stringify(error),
      });
    }
  };

  if (isStripeSdkTimedOut) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.fallbackLink}>
          <span>
            Payment options are taking too long to load. Please complete
            checkout directly on Stripe.
          </span>
          <Button
            className={styles.continueToCheckoutButton}
            onClick={handleFallbackCheckout}
            disabled={isCreatingCheckout}
          >
            {isCreatingCheckout ? 'Redirecting...' : 'Complete Checkout'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit}
      data-e2e="stripe-elements-form"
    >
      {isStripeSdkLoading && <PaymentProgressBar />}

      <ExpressCheckoutElement
        options={{
          paymentMethods: {
            googlePay: 'always',
            link: 'never', // never so it's shown in payment element below
            paypal: 'never',
          },
          paymentMethodOrder: ['apple_pay', 'google_pay', 'amazon_pay'],
          buttonTheme: {
            googlePay: 'white',
            applePay: isDarkTheme ? 'white' : 'black',
          },
          layout: {
            maxColumns: 1,
            maxRows: 5,
          },
          buttonHeight: 53,
        }}
        onReady={() => setReadyExpressCheckout(true)}
        onConfirm={handleSubmit}
        onClick={handleExpressCheckoutClick}
      />
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            // !!! (olha): if we change defaultCollapsed to false, GTM event don't work properly
            defaultCollapsed: true,
            radios: false,
            spacedAccordionItems: true,
          },
          fields: {
            billingDetails: {
              email: 'never',
            },
          },
          paymentMethodOrder: ['google_pay', 'apple_pay', 'card', 'cashapp'],
        }}
        onReady={() => setReadyPaymentElement(true)}
        onChange={handlePaymentElementChange}
      />
      <button
        disabled={isSubmitDisabled}
        className={
          isCashAppButton
            ? cn(styles.submitButton, styles.blackSubmitButton)
            : cn(styles.submitButton, styles.defaultSubmitButton)
        }
        type="submit"
        onClick={handleSubmitClick}
        data-e2e="subscribe-submit-button"
      >
        {isCashAppButton ? 'Reveal QR Code' : 'Subscribe'}
      </button>
    </form>
  );
};
